/**
 * Serial upload queue with hard caps (backpressure for recording chunks).
 *
 * - One upload runs at a time, so chunks never compete for the student's uplink.
 * - maxItems / maxBytes cap the chunks WAITING behind the in-flight one. Worst-case memory is therefore
 *   maxBytes + one in-flight chunk.
 * - When a new chunk would exceed a cap, the oldest waiting chunks are dropped (the in-flight upload is never
 *   cancelled) and reported through onDrop, so the most recent footage is kept.
 * - upload(item) resolves true on success; false / throw counts as a failed item (its own retries are the
 *   caller's responsibility).
 */
export function createBoundedUploadQueue({ upload, maxItems, maxBytes, onDrop = () => {}, onSettled = () => {} }) {
  const waiting = [];
  let waitingBytes = 0;
  let running = false;
  let idleWaiters = [];
  const counters = { enqueued: 0, uploaded: 0, failed: 0, dropped: 0, droppedBytes: 0 };

  const drop = (item, reason) => {
    counters.dropped += 1;
    counters.droppedBytes += item.size || 0;
    onDrop(item, reason, stats());
  };

  const stats = () => ({ ...counters, waiting: waiting.length, waitingBytes, inFlight: running });

  const pump = async () => {
    if (running) return;
    running = true;
    while (waiting.length) {
      const item = waiting.shift();
      waitingBytes -= item.size || 0;
      let ok = false;
      try {
        ok = !!(await upload(item));
      } catch (err) {
        ok = false;
        console.warn('[Recording] chunk upload threw:', err?.message || err);
      }
      counters[ok ? 'uploaded' : 'failed'] += 1;
      onSettled(item, ok, stats());
    }
    running = false;
    idleWaiters.splice(0).forEach((resolve) => resolve(true));
  };

  return {
    enqueue(item) {
      const size = item.size || 0;
      counters.enqueued += 1;
      if (size > maxBytes) {
        drop(item, 'chunk larger than queue byte limit');
        return false;
      }
      while (waiting.length && (waiting.length + 1 > maxItems || waitingBytes + size > maxBytes)) {
        const oldest = waiting.shift();
        waitingBytes -= oldest.size || 0;
        drop(oldest, 'queue full');
      }
      waiting.push(item);
      waitingBytes += size;
      pump();
      return true;
    },
    /** Resolves true once nothing is waiting or in flight, false after timeoutMs. */
    drain(timeoutMs) {
      if (!running && !waiting.length) return Promise.resolve(true);
      return new Promise((resolve) => {
        const timer = setTimeout(() => {
          idleWaiters = idleWaiters.filter((w) => w !== done);
          resolve(false);
        }, timeoutMs);
        const done = () => { clearTimeout(timer); resolve(true); };
        idleWaiters.push(done);
      });
    },
    stats,
  };
}
